import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { isNativeApp } from '../utils/platform';
import { notifyError } from '../lib/notify';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// On the native mobile app, lift floating UI above the bottom tab bar.
const FLOAT_BOTTOM = isNativeApp()
  ? 'calc(env(safe-area-inset-bottom) + 80px)'
  : '1.5rem';

const SupportChat = ({ user }) => {
  const native = isNativeApp();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('menu'); // menu | tickets | chat | newTicket | contactOptions
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [firstMessage, setFirstMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${API}/public/company-info`).then(r => setCompanyInfo(r.data)).catch(() => {});
  }, []);

  // Allow other parts of the app (Help page, menu drawer) to open the support
  // panel imperatively — this works even when the floating FAB is hidden (native app).
  useEffect(() => {
    const handler = (e) => {
      const v = (e && e.detail && e.detail.view) || 'menu';
      setIsOpen(true);
      setView(v);
      if (user && token) fetchTickets();
    };
    window.addEventListener('open-support-chat', handler);
    return () => window.removeEventListener('open-support-chat', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  useEffect(() => {
    if (isOpen && user && token) fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (activeTicket && view === 'chat') {
      fetchMessages(activeTicket.id);
      pollRef.current = setInterval(() => fetchMessages(activeTicket.id), 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTicket, view]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/support/tickets`, { headers });
      setTickets(res.data);
    } catch (e) { /* ignore */ }
  };

  const fetchMessages = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/support/tickets/${ticketId}/messages`, { headers });
      setMessages(res.data);
    } catch (e) { /* ignore */ }
  };

  const createTicket = async () => {
    if (!subject.trim() || !firstMessage.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/support/tickets`, { subject, message: firstMessage }, { headers });
      const ticket = { id: res.data.id, subject: res.data.subject, status: 'open' };
      setActiveTicket(ticket);
      setView('chat');
      setSubject('');
      setFirstMessage('');
      fetchTickets();
    } catch (e) { notifyError(e, 'Could not create your support ticket.'); }
    setSending(false);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeTicket) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/tickets/${activeTicket.id}/messages`, { message: newMessage }, { headers });
      setNewMessage('');
      fetchMessages(activeTicket.id);
    } catch (e) { notifyError(e, 'Message failed to send.'); }
    setSending(false);
  };

  const openTicketChat = (ticket) => {
    setActiveTicket(ticket);
    setView('chat');
  };

  if (!user) return null;

  // Owner-controlled visibility for the support contact options.
  const showChatOpt = companyInfo.show_support_chat !== false;
  const showEmailOpt = !!companyInfo.contact_email && companyInfo.show_email !== false;
  const showCallOpt = !!companyInfo.contact_phone && companyInfo.show_phone !== false;
  const noContactOptions = !showChatOpt && !showEmailOpt && !showCallOpt;

  return (
    <>
      {/* Floating button — hidden on the native app (opened via Help page / menu instead) */}
      {!isOpen && !native && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed right-6 w-14 h-14 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-xl flex items-center justify-center z-50 transition-transform hover:scale-110"
          style={{ bottom: FLOAT_BOTTOM }}
          data-testid="support-chat-fab"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="fixed right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-96 max-h-[520px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-50 overflow-hidden" style={{ bottom: FLOAT_BOTTOM }} data-testid="support-chat-panel">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              {view !== 'menu' && view !== 'contactOptions' && (
                <button onClick={() => { setView(activeTicket && view === 'chat' ? 'tickets' : 'menu'); setActiveTicket(null); }} className="text-white hover:text-orange-100">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
              )}
              {view === 'contactOptions' && (
                <button onClick={() => setView('menu')} className="text-white hover:text-orange-100">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
              )}
              <h3 className="text-white font-semibold text-sm">
                {view === 'menu' ? 'Support' : view === 'contactOptions' ? 'Contact Us' : view === 'tickets' ? 'My Tickets' : view === 'newTicket' ? 'New Ticket' : activeTicket?.subject || 'Chat'}
              </h3>
            </div>
            <button onClick={() => { setIsOpen(false); setView('menu'); setActiveTicket(null); }} className="text-white hover:text-orange-100" data-testid="support-chat-close">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">

            {/* MENU */}
            {view === 'menu' && (
              <div className="p-4 space-y-3">
                <p className="text-gray-600 text-sm mb-2">How can we help you today?</p>

                <button onClick={() => setView('contactOptions')} className="w-full text-left bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl p-4 transition" data-testid="support-contact-btn">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800 text-sm">Contact Support</div>
                      <div className="text-xs text-gray-500">Message, email, or call us</div>
                    </div>
                  </div>
                </button>

                {showChatOpt && (
                <button onClick={() => { fetchTickets(); setView('newTicket'); }} className="w-full text-left bg-green-50 hover:bg-green-100 border border-green-200 rounded-xl p-4 transition" data-testid="support-send-msg-btn">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800 text-sm">Send a Message</div>
                      <div className="text-xs text-gray-500">Start a support conversation</div>
                    </div>
                  </div>
                </button>
                )}

                {showEmailOpt && (
                <button
                  onClick={() => { window.location.href = `mailto:${companyInfo.contact_email}`; }}
                  className="w-full text-left bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-xl p-4 transition"
                  data-testid="support-email-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800 text-sm">Email Support</div>
                      <div className="text-xs text-gray-500">{companyInfo.contact_email}</div>
                    </div>
                  </div>
                </button>
                )}

                {tickets.filter(t => t.status === 'open').length > 0 && (
                  <button onClick={() => { fetchTickets(); setView('tickets'); }} className="w-full text-left bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-xl p-3 transition" data-testid="support-view-tickets-btn">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-800 text-sm">View Open Tickets</span>
                      <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">{tickets.filter(t => t.status === 'open').length}</span>
                    </div>
                  </button>
                )}
              </div>
            )}

            {/* CONTACT OPTIONS */}
            {view === 'contactOptions' && (
              <div className="p-4 space-y-3">
                <p className="text-gray-600 text-sm mb-2">Choose how to contact us:</p>

                {showChatOpt && (
                <button onClick={() => { fetchTickets(); setView('newTicket'); }} className="w-full text-left bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl p-4 transition" data-testid="contact-message-btn">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800 text-sm">Message</div>
                      <div className="text-xs text-gray-500">Chat with our support team</div>
                    </div>
                  </div>
                </button>
                )}

                {showEmailOpt && (
                <button
                  onClick={() => { window.location.href = `mailto:${companyInfo.contact_email}`; }}
                  className="w-full text-left bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-xl p-4 transition"
                  data-testid="contact-email-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800 text-sm">Email</div>
                      <div className="text-xs text-gray-500">{companyInfo.contact_email}</div>
                    </div>
                  </div>
                </button>
                )}

                {showCallOpt && (
                <button
                  onClick={() => { window.location.href = `tel:${companyInfo.contact_phone}`; }}
                  className="w-full text-left bg-green-50 hover:bg-green-100 border border-green-200 rounded-xl p-4 transition"
                  data-testid="contact-call-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800 text-sm">Call</div>
                      <div className="text-xs text-gray-500">{companyInfo.contact_phone}</div>
                    </div>
                  </div>
                </button>
                )}

                {noContactOptions && (
                  <div className="text-center py-8 text-gray-400 text-sm" data-testid="contact-none">
                    Support is currently unavailable. Please check back later.
                  </div>
                )}
              </div>
            )}

            {/* TICKETS LIST */}
            {view === 'tickets' && (
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-gray-600 text-sm">Your support tickets</p>
                  <button onClick={() => setView('newTicket')} className="text-orange-500 text-sm font-semibold hover:text-orange-600">+ New</button>
                </div>
                {tickets.filter(t => t.status === 'open').length === 0 && tickets.filter(t => t.status === 'closed').length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-sm">No tickets yet</div>
                ) : (
                  <div className="space-y-2">
                    {/* Open tickets */}
                    {tickets.filter(t => t.status === 'open').map(t => (
                      <button key={t.id} onClick={() => openTicketChat(t)} className="w-full text-left bg-orange-50 hover:bg-orange-100 rounded-lg p-3 transition border border-orange-200" data-testid={`ticket-${t.id}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-800 text-sm truncate flex-1">{t.subject}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full ml-2 bg-green-100 text-green-700">open</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {t.id.slice(0, 8).toUpperCase()} &middot; {new Date(t.created_at).toLocaleDateString()}
                        </div>
                      </button>
                    ))}

                    {tickets.filter(t => t.status === 'open').length === 0 && (
                      <div className="text-center py-4 text-gray-400 text-xs">No open tickets</div>
                    )}

                    {/* Closed tickets - collapsible history */}
                    {tickets.filter(t => t.status === 'closed').length > 0 && (
                      <div className="pt-2 border-t mt-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); const el = e.currentTarget.nextElementSibling; el.style.display = el.style.display === 'none' ? 'block' : 'none'; }}
                          className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 mb-2"
                          data-testid="chat-history-toggle"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                          History ({tickets.filter(t => t.status === 'closed').length})
                        </button>
                        <div style={{ display: 'none' }} className="space-y-2">
                          {tickets.filter(t => t.status === 'closed').map(t => (
                            <button key={t.id} onClick={() => openTicketChat(t)} className="w-full text-left bg-gray-50 hover:bg-gray-100 rounded-lg p-3 transition border opacity-60" data-testid={`ticket-closed-${t.id}`}>
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-gray-500 text-sm truncate flex-1">{t.subject}</span>
                                <span className="text-xs px-2 py-0.5 rounded-full ml-2 bg-gray-200 text-gray-500">closed</span>
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                {t.id.slice(0, 8).toUpperCase()} &middot; {new Date(t.created_at).toLocaleDateString()}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* NEW TICKET */}
            {view === 'newTicket' && (
              <div className="p-4 space-y-3">
                <div>
                  <label className="block text-gray-700 text-xs font-medium mb-1">Subject</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    placeholder="Brief description of your issue..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                    data-testid="support-subject-input"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-xs font-medium mb-1">Message</label>
                  <textarea
                    value={firstMessage}
                    onChange={e => setFirstMessage(e.target.value)}
                    placeholder="Describe your issue in detail..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
                    data-testid="support-first-msg-input"
                  />
                </div>
                <button
                  onClick={createTicket}
                  disabled={sending || !subject.trim() || !firstMessage.trim()}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2.5 rounded-lg font-semibold text-sm disabled:opacity-50 transition"
                  data-testid="support-create-ticket-btn"
                >
                  {sending ? 'Sending...' : 'Send Message'}
                </button>

                {tickets.filter(t => t.status === 'open').length > 0 && (
                  <div className="pt-2 border-t">
                    <button onClick={() => setView('tickets')} className="text-orange-500 text-sm font-medium hover:underline">
                      View existing tickets ({tickets.filter(t => t.status === 'open').length} open)
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* CHAT */}
            {view === 'chat' && activeTicket && (
              <div className="flex flex-col h-[380px]">
                {/* Ticket info bar */}
                <div className="px-4 py-2 bg-gray-50 border-b flex items-center justify-between flex-shrink-0">
                  <div>
                    <span className="text-xs text-gray-400">Ticket #{activeTicket.id?.slice(0, 8).toUpperCase()}</span>
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${activeTicket.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                      {activeTicket.status || 'open'}
                    </span>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="support-messages">
                  {messages.map(msg => (
                    <div key={msg.id} className={`flex ${msg.sender_role === 'admin' ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${
                        msg.sender_role === 'admin'
                          ? 'bg-orange-100 text-gray-800 rounded-bl-none'
                          : 'bg-blue-500 text-white rounded-br-none'
                      }`}>
                        {msg.sender_role === 'admin' && (
                          <div className="text-xs font-semibold text-orange-600 mb-0.5">Admin</div>
                        )}
                        <div>{msg.message}</div>
                        <div className={`text-[10px] mt-1 ${msg.sender_role === 'admin' ? 'text-gray-400' : 'text-blue-100'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                {(activeTicket.status || 'open') === 'open' ? (
                  <div className="px-4 py-3 border-t bg-white flex-shrink-0">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={e => setNewMessage(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                        placeholder="Type a message..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                        data-testid="support-msg-input"
                      />
                      <button
                        onClick={sendMessage}
                        disabled={sending || !newMessage.trim()}
                        className="bg-orange-500 hover:bg-orange-600 text-white p-2 rounded-full disabled:opacity-50 transition"
                        data-testid="support-send-btn"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-3 border-t bg-gray-50 text-center text-sm text-gray-400 flex-shrink-0">
                    This ticket has been closed by admin.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChat;
